import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import DebounceSearchInput from "components/atoms/input/search/DebounceSearchInput";
import React from "react";
import { getAlertRuleAccountList } from "api/alertAPI";
import {
  AlertRuleAccountInfo,
  AlertRuleAccountListResponse,
} from "api/interfaces/alertInterface.interface";
import { TableColumnsType } from "antd";
import {
  changeLevelFromABBToABB,
  matchStringSearchKeyword,
} from "utils/functions";
import SuspendedLabel from "components/atoms/text/labels/SuspendedLabel";
import { SearchVirtualTable } from "./SearchVirtualTable";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { changeSuspensionStatus, checkIsSuspended } from "utils/AccountUtil";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";

export type SearchAlertAccountProps = {
  accountId: string;
  placeholder?: string;
  selectedAccountId?: string[];
  disabledAccountId?: string[];
  isSingleMode?: boolean;
  onSelectedRowsChange?: (selectedRows: string[]) => void;
  disabled?: boolean;
  allSelect?: boolean;
  rowSelectable?: (record: ModalAccount) => boolean;
  rowExpandable?: (record: ModalAccount) => boolean;
  checkDisabled?: (record: ModalAccount) => boolean;
};

export interface DataRow {
  id: string;
  name: string;
  level: string;
  status: number;
}

export interface ModalAccount {
  key: string;
  name: string;
  level: string;
  status: number;
  parentLevel: LEVEL_TYPE;
  subRows?: ModalAccount[];
}

/** API에서 내려온 accountId와 테이블 row key(리프는 accountId_LEVEL, 자식 EU는 accountId만)를 맞춤 */
function resolveSearchAlertAccountRowKey(
  accounts: ModalAccount[],
  targetAccountId: string
): string {
  if (!targetAccountId || accounts.length === 0) {
    return targetAccountId;
  }

  const walkExact = (rows: ModalAccount[]): string | undefined => {
    for (const row of rows) {
      if (row.key === targetAccountId) {
        return row.key;
      }
      if (row.subRows?.length) {
        const found = walkExact(row.subRows);
        if (found) return found;
      }
    }
    return undefined;
  };

  const walkLeafWithSuffix = (rows: ModalAccount[]): string | undefined => {
    for (const row of rows) {
      if (row.key.startsWith(`${targetAccountId}_`)) {
        return row.key;
      }
      if (row.subRows?.length) {
        const found = walkLeafWithSuffix(row.subRows);
        if (found) return found;
      }
    }
    return undefined;
  };

  return walkExact(accounts) ?? walkLeafWithSuffix(accounts) ?? targetAccountId;
}

export function SearchAlertAccount(
  props: SearchAlertAccountProps
): JSX.Element {
  const [accounts, setAccounts] = useState<ModalAccount[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    props.selectedAccountId ?? []
  );
  const [pending, setPending] = useState<boolean>(true);

  const columns: TableColumnsType<ModalAccount> = [
    {
      dataIndex: "name",
      title: "Name",
      render: (value) => value,
      className: "name",
    },
    {
      dataIndex: "level",
      title: "Level",
      width: 50,
      render: (value, row) =>
        changeLevelFromABBToABB({ level: value, parentLevel: row.parentLevel }),
    },
    {
      dataIndex: "status",
      title: "Status",
      render: (value, row) => (
        <SuspendedLabel status={changeSuspensionStatus(value)} />
      ),
      width: 80,
    },
  ];
  const { isLoading } = useQuery(
    ["account", props.accountId],
    () =>
      getAlertRuleAccountList({
        accountId: props.accountId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: AlertRuleAccountListResponse) => {
        if (res.result === undefined) {
          setAccounts([]);
          return;
        } else {
          const totalList: ModalAccount[] = [];

          res.result.forEach((data: AlertRuleAccountInfo, index: number) => {
            if (data.subRow === undefined) {
              const model = {
                key: data.accountId.concat(`_`, data.level),
                name: data.name,
                level: data.level,
                parentLevel: data.parentLevel,
                status: data.status,
              } as ModalAccount;
              totalList.push(model);
            } else {
              const model = {
                key: data.accountId.concat(`_`, data.level),
                name: data.name,
                level: data.level,
                parentLevel: data.parentLevel,
                status: data.status,
                subRows: data.subRow?.map(
                  (item: AlertRuleAccountInfo, index2: number) => {
                    const subStatus = checkIsSuspended({
                      isSuspended: item.status,
                      cascadedSuspension: data.status,
                    });
                    return {
                      key: item.accountId,
                      name: item.name,
                      level: item.level,
                      parentLevel: data.parentLevel,
                      status:
                        Object.values(SUSPENSION_STATUS).indexOf(subStatus),
                    };
                  }
                ),
              } as ModalAccount;
              totalList.push(model);
            }
          });
          setAccounts(totalList);
        }
      },
      onError: (e: any) => {
        setAccounts([]);
      },
      onSettled: () => {
        setPending(false);
      },
    }
  );

  useEffect(() => {
    if (props.allSelect) {
      let checkedRecorder: string[] = accounts.map(
        (account) => account.key as string
      );
      if (props.onSelectedRowsChange) {
        props.onSelectedRowsChange(checkedRecorder);
      }
    }
  }, [props.allSelect, isLoading, props, accounts]);

  useEffect(() => {
    if (!props.selectedAccountId?.length) {
      setSelectedRowKeys([]);
      return;
    }
    if (props.isSingleMode) {
      const raw = String(props.selectedAccountId[0]);
      const resolved = resolveSearchAlertAccountRowKey(accounts, raw);
      setSelectedRowKeys([resolved]);
      return;
    }
    const resolved = props.selectedAccountId.map((id) =>
      resolveSearchAlertAccountRowKey(accounts, String(id))
    );
    setSelectedRowKeys(resolved);
  }, [props.selectedAccountId, accounts, props.isSingleMode]);

  const onClickSearch = (keyword: string) => {
    setSearchText(keyword);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    if (props.onSelectedRowsChange)
      props.onSelectedRowsChange(newSelectedRowKeys as string[]);
  };

  const data: ModalAccount[] = useMemo(() => {
    return accounts.filter((rec) => {
      return matchStringSearchKeyword(rec.name, searchText);
    });
  }, [searchText, accounts]);

  return (
    <>
      <SearchWrapper>
        <DebounceSearchInput
          placeholder={props.placeholder}
          onClickSearch={onClickSearch}
          data-testid="search-alert-account-input"
        />
      </SearchWrapper>
      <SearchVirtualTable<ModalAccount>
        columns={columns}
        dataSource={data}
        loading={pending}
        isSingleMode={props.isSingleMode}
        useRowExpandedSelection={true}
        expandedRowClassName={(record) =>
          record.subRows ? "expander-children-row" : ""
        }
        rowSelectable={props.rowSelectable ?? (() => true)}
        selectedRowKeys={selectedRowKeys}
        onSelectChange={onSelectChange}
        useExpandable={props.rowExpandable ? true : false}
        rowExpandable={props.rowExpandable ?? (() => false)}
        checkDisabled={(record) =>
          Boolean(props.disabled) || (props.checkDisabled?.(record) ?? false)
        }
      />
    </>
  );
}

const SearchWrapper = styled.div`
  ${FLEX.FlexEndCenter};
  padding-bottom: 10px;
  width: 100%;
  .search-input-wrapper {
    width: auto;
    flex: none;
  }
`;
